import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { FileText, Download, Shield, Lock, Eye, Scale, AlertTriangle } from 'lucide-react';

const GovernancePage: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const governance = t?.about?.governance;

    if (!governance) return null;

    const DocumentIcon = ({ title }: { title: string }) => {
        if (title.toLowerCase().includes('estatuto')) return <FileText className="w-5 h-5" />;
        if (title.toLowerCase().includes('política')) return <Lock className="w-5 h-5" />;
        return <FileText className="w-5 h-5" />;
    };

    useSEO({
        title: governance.title,
        description: governance.subtitle,
        path: '/about/governance',
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Sobre', url: '/about' },
                { name: 'Governança', url: '/about/governance' }
            ]),
            generateWebPageSchema({
                name: governance.title,
                description: governance.subtitle,
                url: '/about/governance',
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>

            <main className="flex-grow">
                {/* Hero Section */}
                <div className="bg-primary text-white py-20 bg-gradient-to-br from-dark to-primary-dark relative overflow-hidden">
                    <div className="container mx-auto px-4 text-center relative z-10">
                        <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6 animate-fade-in-up text-transparent bg-clip-text bg-gradient-to-br from-white to-secondary">
                            {governance.title}
                        </h1>
                        <p className="text-xl max-w-3xl mx-auto text-gray-200 animate-slide-up-fade">
                            {governance.subtitle}
                        </p>
                    </div>
                </div>

                {/* Intro Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row gap-12 items-start max-w-4xl mx-auto">
                            <div className="md:w-1/3">
                                <h2 className="text-3xl font-serif font-bold text-primary border-l-4 border-secondary pl-6 sticky top-24">
                                    {governance.intro.title}
                                </h2>
                            </div>
                            <div className="md:w-2/3 space-y-6">
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    {governance.intro.text1}
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    {governance.intro.text2}
                                </p>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                                    {governance.intro.list.map((item: string, index: number) => (
                                        <li key={index} className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                            <div className="w-2 h-2 bg-secondary rounded-full"></div>
                                            <span className="font-semibold text-primary">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Principles Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl font-serif font-bold text-primary text-center mb-16">
                            {governance.principles.title}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {governance.principles.items.map((principle: any, index: number) => {
                                const icons = [Scale, Eye, Shield, Lock];
                                const Icon = icons[index % icons.length];

                                return (
                                    <div key={index} className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-secondary hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center">
                                        <div className="text-primary mb-6 bg-primary/5 w-12 h-12 rounded-full flex items-center justify-center">
                                            <Icon size={24} />
                                        </div>
                                        <h3 className="text-xl font-bold text-primary mb-3">
                                            {principle.title}
                                        </h3>
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                            {principle.description}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Documents Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4 max-w-5xl">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            {governance.documents.categories.map((category: any, index: number) => (
                                <div key={index} className="space-y-6">
                                    <h3 className="text-xl font-bold text-primary border-b border-gray-200 pb-2">
                                        {category.title}
                                    </h3>
                                    <div className="space-y-4">
                                        {category.items.map((doc: any, dIndex: number) => (
                                            <a
                                                key={dIndex}
                                                href={doc.href}
                                                className={`flex items-center justify-between p-4 rounded-lg border transition-all duration-200 group ${doc.name.includes('preparação')
                                                    ? 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-white border-gray-200 hover:border-primary/30 hover:shadow-md text-gray-700 hover:text-primary'
                                                    }`}
                                                onClick={(e) => doc.name.includes('preparação') && e.preventDefault()}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <DocumentIcon title={doc.name} />
                                                    <span className="font-medium text-sm">{doc.name}</span>
                                                </div>
                                                {!doc.name.includes('preparação') && (
                                                    <Download className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Whistleblowing Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                            <div className="bg-primary/5 p-8 text-center border-b border-gray-100">
                                <div className="mx-auto w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
                                    <AlertTriangle size={32} />
                                </div>
                                <h2 className="text-2xl font-bold text-primary mb-3">
                                    {governance.whistleblowing.title}
                                </h2>
                                <p className="text-gray-600">
                                    {governance.whistleblowing.text}
                                </p>
                            </div>
                            <div className="p-8 text-center bg-gray-50">
                                <button
                                    onClick={() => window.open(`mailto:denuncia@nexucapital.com.br?subject=${governance.whistleblowing.emailSubject}`, '_blank')}
                                    className="bg-primary text-white font-bold py-3 px-8 rounded-full hover:bg-secondary transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-flex items-center gap-2"
                                >
                                    {governance.whistleblowing.buttonLabel}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </PublicLayout>
    );
};

export default GovernancePage;